import React from "react";
// Consumer Location Modal

// Default
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

// Mui
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid2";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

// Custom
import CustomInput from "../../forms/input/Input";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomSearchBox from "../search/SearchBox";
import CustomSearch from "../search/Search";
import DatePickerInput from "../../forms/input/DatePickerInput";

// React icons
import { FaRegTrashAlt } from "react-icons/fa";

// Utils
import { fetchGETRequest, fetchPOSTRequest } from "../../utils/services";
import { useCustomFields } from "../../context/CustomFieldsProvider";
import { ToastState } from "../../types";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

const ConsumerLocationModal = ({
  openModal,
  setOpenModal,
  setToast,
  addOrEdit,
  nodes,
  setNodes,
  handleCustomerAndAssetSearch,
}: {
  openModal: { id: string; type: string };
  setOpenModal: (value: { id: string; type: string }) => void;
  setToast: (value: ToastState) => void;
  addOrEdit: "add" | "edit" | null;
  nodes: { id: string; type: string }[];
  setNodes: (value: { id: string; type: string }[]) => void;
  handleCustomerAndAssetSearch: () => void;
}) => {
  const [searchValue, setSearchValue] = useState({
    type: "id",
    value: "",
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Address Details");
  const [names, setNames] = useState<string[]>([]);
  const [counter, setCounter] = useState(1);
  const [siteIdInput, setSiteIdInput] = useState<string[]>([]);
  const [locationData, setLocationData] = useState<{
    id: { id: string; entityType: string };
    consumerType: { value: string; label: string };
    houseNo: string;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    latitude: string;
    longitude: string;
    divisionCode: string;
    subDivisionCode: string;
    numberOfMeters: string;
    [key: string]: any;
  }>({
    id: { id: "", entityType: "" },
    consumerType: { value: "", label: "" },
    houseNo: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    latitude: "",
    longitude: "",
    divisionCode: "",
    subDivisionCode: "",
    numberOfMeters: "",
  });
  const [linkToCustomer, setLinkToCustomer] = useState("");

  const { toggleCustomFieldDrawer, createdFields } = useCustomFields();

  const navigate = useNavigate();

  const TABS = ["Address Details", "Coordinates", "Sites"];

  const consumerTypeOptions = [
    { label: "Personal", value: "Personal" },
    { label: "Business", value: "Business" },
  ];

  const handleAddRow = () => {
    setSiteIdInput((prevInputs) => [...prevInputs, ""]);
  };

  const handleRemoveRow = (index: number) => {
    setSiteIdInput((prevInputs) => prevInputs.filter((_, i) => i !== index));
  };

  const handleSiteIdInputChange = (value: string, index: number) => {
    setSiteIdInput((prevInputs) => {
      const newInputs = [...prevInputs];
      newInputs[index] = value;
      return newInputs;
    });
  };

  const handleGoToSite = (siteId: string) => {
    setSiteIdInput(siteIdInput);
    navigate("/site", { state: { siteId } });
  };

  const generateUniqueName = () => {
    const newName = `Loc${new Date().getMilliseconds()}`;
    setNames([...names, newName]);
    setCounter(counter + 1);
    return newName;
  };

  const handleUpdate = async () => {
    try {
      const requestData = {
        id: {
          id: "",
          entityType: "",
        },
        tenantId: {
          id: "e936e050-e1d7-11ee-8baa-37d11d926d57",
          entityType: "TENANT",
        },
        name: generateUniqueName(),
        type: "LOCATION",
        label: "NY Building",
        additionalInfo: {
          consumerType: locationData?.consumerType?.value,
          streetAddress: locationData?.streetAddress,
          houseNo: locationData?.houseNo,
          state: locationData?.state,
          city: locationData?.city,
          zip: locationData?.zipCode,
          siteIdInput: siteIdInput,
          latitude: locationData?.latitude,
          longitude: locationData?.longitude,
          divisionCode: locationData?.divisionCode,
          subDivisionCode: locationData?.subDivisionCode,
          numberOfMeters: locationData?.numberOfMeters,
        },
      };

      if (locationData?.id && locationData?.id?.id && addOrEdit === "edit") {
        requestData.id = {
          id: locationData?.id?.id,
          entityType: "ASSET",
        };
      }

      let secondResponse = null;
      const response = await fetchPOSTRequest(`asset`, requestData);

      if (response && linkToCustomer) {
        secondResponse = await fetchPOSTRequest(
          `customer/${linkToCustomer}/asset/${response?.id?.id}`,
          requestData,
          true,
        );
      }

      setSearchValue({ ...searchValue, value: response?.id?.id });

      setLocationData({
        id: { id: "", entityType: "" },
        consumerType: { value: "", label: "" },
        houseNo: "",
        streetAddress: "",
        city: "",
        state: "",
        zipCode: "",
        latitude: "",
        longitude: "",
        divisionCode: "",
        subDivisionCode: "",
        numberOfMeters: "",
      });

      setSiteIdInput([]);
      setLinkToCustomer("");

      if (addOrEdit === "add") {
        const updatedNodes = nodes?.map((node) => {
          if (node?.id?.toUpperCase() === "LOCATION") {
            const newObj = {
              created: true,
              label: "Successfully created",
              link: "",
              title: "Location",
              value: response?.id?.id,
            };
            return {
              ...node,
              data: newObj,
            };
          }
          return node;
        });

        setNodes([...updatedNodes]);
      } else {
        handleCustomerAndAssetSearch();
      }

      setOpenModal({
        type: "",
        id: "",
      });

      if (secondResponse?.status === 200) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.id?.id + " " + "Update successful!",
        });
      } else {
        setToast({
          message: response?.message,
          severity: "error",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error during request",
      });
    }
  };

  const handleLocationSearch = async (payload?: string) => {
    try {
      let searchTerm = "";

      if (searchValue?.value.trim()) {
        searchTerm = searchValue?.value;
      } else if (payload) {
        searchTerm = payload;
      } else {
        setToast({
          message: "Please enter a valid search value.",
          severity: "error",
        });
        return;
      }

      const response = await fetchGETRequest(`asset/${searchTerm}`, {}, true);

      if (response?.data?.additionalInfo) {
        const {
          consumerType,
          houseNo,
          streetAddress,
          city,
          state,
          zip,
          siteIdInput,
          latitude,
          longitude,
          divisionCode,
          subDivisionCode,
          numberOfMeters,
        } = response?.data?.additionalInfo;
        setLocationData({
          id: response?.data?.id,
          consumerType: consumerTypeOptions.find(
            (item) => item.value === consumerType,
          ) || { value: "", label: "" },
          houseNo: houseNo,
          streetAddress: streetAddress,
          city: city,
          state: state,
          zipCode: zip,
          latitude: latitude,
          longitude: longitude,
          divisionCode: divisionCode,
          subDivisionCode: subDivisionCode,
          numberOfMeters: numberOfMeters,
        });
        setSiteIdInput(siteIdInput);
        setLinkToCustomer(response?.data?.customerId?.id);
      } else {
        setToast({ message: "Location not found", severity: "error" });
      }
      setSearchOpen(false);
    } catch (error: any) {
      console.error("Error fetching consumer:", error.message);
    }
  };

  useEffect(() => {
    if (openModal?.id && openModal?.type === "LOCATION") {
      setSearchValue({ ...searchValue, value: openModal?.id });
      handleLocationSearch(openModal?.id);
    }
  }, [openModal]);

  const getCustomFields = (item: any) => {
    switch (item?.type?.label) {
      case "Date":
        return (
          <DatePickerInput
            label={item?.title}
            format={"dd/MM/yyyy"}
            placeholder={"dd/mm/yyyy"}
            value={locationData?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setLocationData({
                ...locationData,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      case "Checkbox":
        return (
          <Stack mt={"30px"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={locationData?.[item?.title?.toUpperCase()] || false}
                  onChange={(event) => {
                    setLocationData({
                      ...locationData,
                      [item?.title?.toUpperCase()]: event.target.checked,
                    });
                  }}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": {
                      color: "#1976d2",
                    },
                  }}
                />
              }
              label={item?.title}
            />
          </Stack>
        );
      case "Text":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="text"
              value={locationData?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setLocationData({
                  ...locationData,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Number":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="number"
              value={locationData?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setLocationData({
                  ...locationData,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Dropdown":
        return (
          <>
            <CustomOutlinedSelect
              label={item?.title}
              placeholder={item?.title}
              options={item?.options}
              value={locationData?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setLocationData({
                  ...locationData,
                  [item?.title?.toUpperCase()]: event,
                });
              }}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      onClose={() => setOpenModal({ type: "", id: "" })}
      open={openModal?.type === "LOCATION"}
      fullWidth
      maxWidth={"md"}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>Location Information</DialogTitle>
        <IconButton
          onClick={() => setOpenModal({ type: "", id: "" })}
          sx={{ marginRight: "10px" }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent dividers>
        <Card
          sx={{
            overflow: "visible",
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body1" fontWeight={600}>
            Location ID
          </Typography>

          <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
            <CustomSearchBox
              placeholder={"Enter 10 Digit"}
              width={"500px"}
              height={"40px"}
              handleSearchToggle={() => setSearchOpen(!searchOpen)}
              searchOpen={searchOpen}
              dialogBody={
                <Stack direction={"column"} gap={"20px"}>
                  <CustomInput
                    label={"Type"}
                    value={searchValue?.type}
                    onChange={(e) =>
                      setSearchValue({
                        ...searchValue,
                        type: e.target.value,
                      })
                    }
                  />
                  <CustomInput
                    label={"Value"}
                    value={searchValue?.value}
                    onChange={(e) =>
                      setSearchValue({
                        ...searchValue,
                        value: e.target.value,
                      })
                    }
                  />
                  <ContainedButton
                    label={"Submit"}
                    sx={{
                      width: "100%",
                      fontWeight: 600,
                      color: "#ffffff",
                    }}
                    onClick={handleLocationSearch}
                  />
                </Stack>
              }
            />
          </Stack>
        </Card>

        <Card sx={{ mt: "20px" }}>
          <TabContext value={activeTab}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "#CACDD7",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <TabList
                onChange={(_, newValue) => setActiveTab(newValue)}
                scrollButtons={false}
                TabIndicatorProps={{
                  style: {
                    backgroundColor: "##1280E7",
                    height: "3px",
                  },
                }}
              >
                {TABS.map((item, index) => {
                  return (
                    <Tab
                      key={index}
                      label={
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={"7px"}
                          fontWeight={500}
                        >
                          {item}
                        </Box>
                      }
                      value={item}
                      className="consumer_right_card_list_tab"
                    />
                  );
                })}
              </TabList>

              <ContainedButton
                label={"Create custom fields"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
                onClick={() => toggleCustomFieldDrawer("Location", true)}
              />
            </Box>

            <CardContent>
              <TabPanel value={TABS[0]} sx={{ padding: 0 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomOutlinedSelect
                      label={"Consumer Type"}
                      options={consumerTypeOptions}
                      value={locationData?.consumerType}
                      name={"consumerType"}
                      placeholder={"Select"}
                      onChange={(newValue) =>
                        setLocationData({
                          ...locationData,
                          consumerType: {
                            value: newValue.value.toString(),
                            label: newValue.label,
                          },
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"House No."}
                      value={locationData?.houseNo}
                      placeholder={"House No."}
                      name={"houseNo"}
                      onChange={(e) =>
                        setLocationData({
                          ...locationData,
                          houseNo: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Street Address"}
                      value={locationData?.streetAddress}
                      placeholder={"Street Address"}
                      name={"streetAddress"}
                      onChange={(e) =>
                        setLocationData({
                          ...locationData,
                          streetAddress: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"City"}
                      placeholder={"City"}
                      name={"city"}
                      value={locationData?.city}
                      onChange={(e) =>
                        setLocationData({
                          ...locationData,
                          city: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"State"}
                      placeholder={"State"}
                      name={"state"}
                      value={locationData?.state}
                      onChange={(e) =>
                        setLocationData({
                          ...locationData,
                          state: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Zip Code"}
                      placeholder={"Zip Code"}
                      name={"zipCode"}
                      value={locationData?.zipCode}
                      onChange={(e) =>
                        setLocationData({
                          ...locationData,
                          zipCode: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Link to Consumer"}
                      placeholder={"Link to Consumer"}
                      name={"linkToCustomer"}
                      onChange={(e) => setLinkToCustomer(e.target.value)}
                      value={linkToCustomer}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Division Code"}
                      placeholder={"Division Code"}
                      name={"divisionCode"}
                      onChange={(e) =>
                        setLocationData({
                          ...locationData,
                          divisionCode: e.target.value,
                        })
                      }
                      value={locationData?.divisionCode}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Sub Division Code"}
                      placeholder={"Sub Division Code"}
                      name={"subDivisionCode"}
                      onChange={(e) =>
                        setLocationData({
                          ...locationData,
                          subDivisionCode: e.target.value,
                        })
                      }
                      value={locationData?.subDivisionCode}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Number of Meters at Single Location"}
                      placeholder={"Number of Meters"}
                      name={"numberOfMeters"}
                      onChange={(e) =>
                        setLocationData({
                          ...locationData,
                          numberOfMeters: e.target.value,
                        })
                      }
                      value={locationData?.numberOfMeters}
                    />
                  </Grid>
                </Grid>

                {createdFields?.["Location"] && (
                  <>
                    <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                      <Divider sx={{ borderWidth: "1.5px" }} />
                    </Stack>

                    <Grid container spacing={2}>
                      {createdFields?.["Location"]?.map(
                        (item: any, index: number) => {
                          return (
                            <Grid
                              size={{ xs: 12, md: 4, lg: 4 }}
                              mt={"10px"}
                              key={index}
                            >
                              {getCustomFields(item)}
                            </Grid>
                          );
                        },
                      )}
                    </Grid>
                  </>
                )}
              </TabPanel>

              <TabPanel value={TABS[1]} sx={{ padding: 0 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Latitude"}
                      placeholder={"Latitude"}
                      name={"latitude"}
                      value={locationData?.latitude}
                      onChange={(e) =>
                        setLocationData({
                          ...locationData,
                          latitude: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Longitude"}
                      placeholder={"Longitude"}
                      name={"longitude"}
                      onChange={(e) =>
                        setLocationData({
                          ...locationData,
                          longitude: e.target.value,
                        })
                      }
                      value={locationData?.longitude}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={TABS[2]} sx={{ padding: 0 }}>
                <Box display={"flex"} gap={"20px"}>
                  <Stack width={"60%"}>
                    {siteIdInput.map((siteId, index) => (
                      <Stack
                        key={index}
                        direction={"row"}
                        gap={"20px"}
                        alignItems={"center"}
                        sx={{ marginBottom: "16px" }}
                      >
                        <Typography variant="body1" fontWeight={600}>
                          Site ID:
                        </Typography>

                        <CustomSearch
                          placeholder={"10 digits"}
                          sx={{
                            width: "400px",
                            height: "40px",
                          }}
                          value={siteId || ""}
                          name={`siteIdInput-${index}`}
                          onChange={(e) =>
                            handleSiteIdInputChange(e.target.value, index)
                          }
                          onClick={() => handleGoToSite(siteId)}
                        />

                        <Stack direction={"row"} gap={"10px"}>
                          <ContainedButton
                            label={"Go to Site"}
                            sx={{
                              width: "fit-content",
                              fontWeight: 600,
                              height: "35px",
                              color: "#ffffff",
                            }}
                            onClick={() => handleGoToSite(siteId)}
                          />
                          <Box
                            sx={{
                              backgroundColor: "#FA6B6B",
                              width: "35px",
                              height: "35px",
                              borderRadius: "4px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                            onClick={() => handleRemoveRow(index)}
                          >
                            <UnifiedIcon
                              icon={FaRegTrashAlt}
                              color="#ffffff"
                              style={{ marginBottom: "2px" }}
                            />
                          </Box>
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                </Box>

                <ContainedButton
                  label={"Add Row"}
                  sx={{
                    width: "fit-content",
                    fontWeight: 600,
                    color: "#ffffff",
                  }}
                  onClick={handleAddRow}
                />
              </TabPanel>

              {/* <Stack
              direction={"direction"}
              justifyContent={"space-between"}
              justifyItems={"center"}
              mt={"20px"}
            >

              <ContainedButton
                label={"Go to Commitment Contract"}
                
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  height: "35px",
                  color: "#ffffff",
                }}
                onClick={() => navigate("/commitment")}
              />

              <ContainedButton
                label={"Add New Site"}
                
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  height: "35px",
                  color: "#ffffff",
                }}
                onClick={() => navigate("/site")}
              />
            </Stack> */}
            </CardContent>
          </TabContext>
        </Card>

        <DialogActions>
          <ContainedButton
            label={"Save"}
            sx={{
              width: "fit-content",
              fontWeight: 600,
              color: "#ffffff",
            }}
            onClick={handleUpdate}
          />
          <ContainedButton
            label={"Close"}
            bgColor={"linear-gradient(180deg, #FF7F7F 0%, #FF4D4D 100%)"}
            sx={{
              fontWeight: 600,
              color: "#ffffff",
            }}
            onClick={() => setOpenModal({ type: "", id: "" })}
          />
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default ConsumerLocationModal;
